import { useState } from 'react';
import { Info } from 'lucide-react';
import { Accordion } from 'components/Accordion';
import { Tooltip } from 'common/components';
import { CallStatistic } from 'types/call.type';
import styles from './Statistic.module.scss';

export const Statistic = ({ callStats }: { callStats?: CallStatistic }) => {
    const [isAccordionOpen, setIsAccordionOpen] = useState(true);

    const {
        overDue,
        pending,
        ringing,
        inProgress,
        failed,
        busy,
        noAnswer,
        voicemail,
        voicemailSuccess,
        voicemailFailed,
        userAbandoned,
        agentAbandoned,
        transferCompleted,
        transferFailed,
        transferInProgress,
        transferUserAbandoned,
        noAgentsAvailable,
        agentRejected,
        humanOnCall,
        total,
        transfer,
        badNumber,
        blocked,
        completed,
        contacted,
    } = callStats || {
        pending: 0,
        overDue: 0,
        ringing: 0,
        inProgress: 0,
        failed: 0,
        busy: 0,
        noAnswer: 0,
        voicemail: 0,
        voicemailSuccess: 0,
        voicemailFailed: 0,
        userAbandoned: 0,
        agentAbandoned: 0,
        transferCompleted: 0,
        transferFailed: 0,
        transferInProgress: 0,
        transferUserAbandoned: 0,
        noAgentsAvailable: 0,
        agentRejected: 0,
        humanOnCall: 0,
        total: 0,
        transfer: 0,
        badNumber: 0,
        blocked: 0,
        completed: 0,
        contacted: 0,
    };

    return (
        <div className={styles.callStatistics}>
            <Accordion
                isOpen={isAccordionOpen}
                onToggle={() => setIsAccordionOpen(!isAccordionOpen)}
                header={
                    <div className={styles.totalCallsLabel}>
                        <span>Call status overview:</span>
                        <span> {total}</span>
                    </div>
                }
                className={styles.accordion}
            >
                <div className={styles.statsCardsGrid}>
                    <div className={`${styles.statCard} ${styles.pendingCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Queued/Overdue
                                <Tooltip
                                    content="Calls ready to dial but waiting for an open slot. If this isn't going down, check that the workflow has agents assigned, those agents are online, and Calls Per Agent is high enough."
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Queued/Overdue info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{overDue + pending}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Queued:</span>
                                <span className={styles.noteValue}>{pending}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Overdue:</span>
                                <span className={styles.noteValue}>{overDue}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.inProgressCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                In Progress
                                <Tooltip
                                    content="Calls currently being handled by the AI agent. If calls aren't reaching this stage, check the Queued/Overdue card for bottlenecks."
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='In Progress info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{ringing + inProgress}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Ringing:</span>
                                <span className={styles.noteValue}>{ringing}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Active:</span>
                                <span className={styles.noteValue}>{inProgress}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.unableCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Unable to contact
                                <Tooltip
                                    content="Calls that didn't connect. A high Bad Number count means your contact list needs cleanup. High Blocked means contacts are on do-not-call or opt-out lists. High No Answer may mean you're calling outside the contact's timezone."
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Unable to contact info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{failed + busy + noAnswer + badNumber + blocked}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Failed:</span>
                                <span className={styles.noteValue}>{failed}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Busy:</span>
                                <span className={styles.noteValue}>{busy}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>No Answer:</span>
                                <span className={styles.noteValue}>{noAnswer}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Bad Number:</span>
                                <span className={styles.noteValue}>{badNumber}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Blocked:</span>
                                <span className={styles.noteValue}>{blocked}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.voicemailCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Voicemail
                                <Tooltip
                                    content='Calls that went to voicemail. Success means a message was left. If Failed is high, check that your voicemail drop audio is configured correctly.'
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Voicemail info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{voicemail}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Success:</span>
                                <span className={styles.noteValue}>{voicemailSuccess}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Failed:</span>
                                <span className={styles.noteValue}>{voicemailFailed}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.contactedCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Contacted
                                <Tooltip
                                    content='Calls where a live conversation happened. High Agent Abandoned may indicate a prompt issue causing the AI to hang up early.'
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Contacted info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{contacted}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>User Abandoned:</span>
                                <span className={styles.noteValue}>{userAbandoned}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Agent Abandoned:</span>
                                <span className={styles.noteValue}>{agentAbandoned}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Completed:</span>
                                <span className={styles.noteValue}>{completed}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.transferCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>
                                Transfer
                                <Tooltip
                                    content='Human handoff outcomes. If No Agents or Failed is high, make sure human agents are online and accepting transfers.'
                                    maxWidth={350}
                                >
                                    <Info
                                        size={14}
                                        className={styles.infoIcon}
                                        tabIndex={0}
                                        aria-label='Transfer info'
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.statValue}>{transfer}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Completed:</span>
                                <span className={styles.noteValue}>{transferCompleted}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Failed:</span>
                                <span className={styles.noteValue}>{transferFailed}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>In Progress:</span>
                                <span className={styles.noteValue}>{transferInProgress}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>No Agents:</span>
                                <span className={styles.noteValue}>{noAgentsAvailable}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Agent Rejected:</span>
                                <span className={styles.noteValue}>{agentRejected}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>User Abandoned:</span>
                                <span className={styles.noteValue}>{transferUserAbandoned}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Human On Call:</span>
                                <span className={styles.noteValue}>{humanOnCall}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Accordion>
        </div>
    );
};
